import * as THREE from "three";
import { defineModel, smokePlume, sway, type Part } from "../../lib";

// Witch Hut — a crooked storybook hut that leans like it is listening: fat
// tilted plaster walls under a SWOLLEN bent witch-hat roof, a stovepipe
// chimney leaning hard out of the brim, a round window glowing potion-green
// and a little lantern hanging by the too-big purple door. The lantern
// ("lantern") swings and the green window ("glow") pulses on the idle loop,
// while the stovepipe puffs its own smoke plume.
//
// Frame: y = 0 is the ground, the door faces +Z. 1 unit = 1 m; the hut is a
// squat ~4 m caricature on a 2x2 tile plot.

const WALL = "#c98a4b";        // warm ochre plaster
const WALL_DARK = "#a76e3a";   // shaded base skirt
const HAT = "#6f4aa8";         // saturated witch-hat purple
const HAT_DARK = "#57398a";    // brim underside / shading
const TRIM = "#f6efe3";        // cream trim band
const DOOR = "#7a4c9e";        // big purple door
const DOOR_DARK = "#4c3168";   // door recess
const GLOW = "#8ef26a";        // potion-green window glow
const WOOD = "#8a5a33";        // bracket, step, window ring
const METAL = "#e8b53a";       // golden lantern cage
const PIPE = "#6b6259";        // stovepipe iron

// The whole hut leans a touch to the left — everything crooked on purpose.
const LEAN = 0.07;

// ── frame heights ────────────────────────────────────────────────────────────
const WALL_H = 2.0;                  // plaster walls, ground to hat brim
const BRIM_Y = WALL_H + 0.1;         // where the swollen brim seats

// The stovepipe mouth the smoke rises from — leaning far out past the brim on
// the left so the puffs read clear of the big purple hat.
const PIPE_MOUTH: [number, number, number] = [-1.85, 3.0, -0.35];

// The lantern hangs from its bracket tip beside the door; it swings about
// that point.
const LANTERN_HOOK: [number, number, number] = [0.9, 1.85, 1.3];

// The glowing round window pulses about its own centre.
const GLOW_AT: [number, number, number] = [-0.7, 1.38, 1.14];

const smoke = smokePlume(PIPE_MOUTH, {
  puffs: 4,
  rise: 1.2,
  drift: [-0.35, -0.15],
  radius: 0.15,
  color: "#cdc6bb",
  duration: 4.8,
});

// The little hanging lantern: a golden cage with a warm flame, all named
// "lantern" so the whole thing swings as one pendulum from the hook.
function lantern(): Part[] {
  const [hx, hy, hz] = LANTERN_HOOK;
  return [
    // Hanging cord from the hook down to the cage.
    { geometry: new THREE.CylinderGeometry(0.02, 0.02, 0.22, 6), color: PIPE,
      position: [hx, hy - 0.11, hz], name: "lantern" },
    // Cage body and cap.
    { geometry: new THREE.CylinderGeometry(0.11, 0.13, 0.24, 8), color: METAL,
      position: [hx, hy - 0.34, hz], name: "lantern" },
    { geometry: new THREE.ConeGeometry(0.14, 0.12, 8), color: METAL,
      position: [hx, hy - 0.19, hz], name: "lantern" },
    // The little flame glowing inside, poking out below.
    { geometry: new THREE.SphereGeometry(0.075, 8, 6), color: GLOW,
      position: [hx, hy - 0.47, hz], name: "lantern" },
  ];
}

export const witchHut = defineModel({
  id: "witch-hut",
  name: "Witch Hut",
  batchId: "mess",
  tags: ["mess", "cute", "animated", "building", "fantasy", "witch", "hut", "magic"],
  summary: "A crooked little hut with a bent witch-hat roof, a glowing green window and a swinging lantern.",
  description:
    "A leaning storybook witch hut: fat ochre plaster walls under a swollen " +
    "bent witch-hat roof, a stovepipe chimney tipping out of the brim, a round " +
    "window glowing potion-green and a golden lantern swinging by the too-big " +
    "purple door. The lantern swings and the window pulses on the idle loop " +
    "while the stovepipe puffs smoke.",
  author: "models",
  footprint: 2.1,
  tiles: { w: 2, h: 2 },
  parts(): Part[] {
    return [
      // ── crooked plaster walls ─────────────────────────────────────────────
      // A fat tapered drum, deliberately leaning to the left.
      { geometry: new THREE.CylinderGeometry(1.15, 1.5, WALL_H, 14), color: WALL,
        position: [0, WALL_H / 2, 0], rotation: [0.03, 0, LEAN] },
      // A shaded skirt planting the base.
      { geometry: new THREE.CylinderGeometry(1.55, 1.65, 0.26, 14), color: WALL_DARK,
        position: [0, 0.13, 0] },
      // Cream trim band where the walls meet the hat.
      { geometry: new THREE.CylinderGeometry(1.2, 1.24, 0.18, 14), color: TRIM,
        position: [-0.13, WALL_H, 0], rotation: [0.03, 0, LEAN] },

      // ── the swollen bent witch hat ────────────────────────────────────────
      // The wide floppy brim, sagging past the walls all round.
      { geometry: new THREE.CylinderGeometry(1.1, 2.15, 0.42, 14), color: HAT,
        position: [-0.16, BRIM_Y + 0.21, 0], rotation: [0.04, 0, LEAN] },
      { geometry: new THREE.CylinderGeometry(2.05, 1.5, 0.14, 14), color: HAT_DARK,
        position: [-0.15, BRIM_Y + 0.02, 0], rotation: [0.04, 0, LEAN] },
      // The cone crown, tilting harder than the walls...
      { geometry: new THREE.ConeGeometry(1.05, 1.5, 12), color: HAT,
        position: [-0.28, BRIM_Y + 1.05, 0], rotation: [0.05, 0, 0.2] },
      // ...and the bent-over tip, folded to the left with a bobble.
      { geometry: new THREE.ConeGeometry(0.42, 0.9, 10), color: HAT,
        position: [-0.85, BRIM_Y + 1.85, 0], rotation: [0, 0, 1.0] },
      { geometry: new THREE.SphereGeometry(0.16, 8, 6), color: METAL,
        position: [-1.28, BRIM_Y + 2.0, 0] },

      // ── the too-big purple door (facing +Z) ───────────────────────────────
      { geometry: new THREE.BoxGeometry(1.0, 1.55, 0.14), color: DOOR_DARK,
        position: [0, 0.78, 1.3] },
      { geometry: new THREE.BoxGeometry(0.84, 1.42, 0.16), color: DOOR,
        position: [0, 0.71, 1.34] },
      // Round-top arch over the door slab.
      { geometry: new THREE.CylinderGeometry(0.42, 0.42, 0.16, 12), color: DOOR,
        position: [0, 1.42, 1.34], rotation: [Math.PI / 2, 0, 0] },
      { geometry: new THREE.SphereGeometry(0.06, 8, 6), color: METAL,
        position: [0.3, 0.78, 1.44] },
      // A chunky wooden step.
      { geometry: new THREE.BoxGeometry(1.1, 0.14, 0.5), color: WOOD,
        position: [0, 0.07, 1.62] },

      // ── the glowing green round window ────────────────────────────────────
      // Wooden ring pressed into the front wall, with the pulsing pane inside.
      { geometry: new THREE.CylinderGeometry(0.34, 0.34, 0.16, 12), color: WOOD,
        position: [GLOW_AT[0], GLOW_AT[1], GLOW_AT[2] - 0.05], rotation: [Math.PI / 2, 0, 0] },
      { geometry: new THREE.CylinderGeometry(0.24, 0.24, 0.18, 12), color: GLOW,
        position: GLOW_AT, rotation: [Math.PI / 2, 0, 0], name: "glow" },

      // ── lantern bracket + the swinging lantern ────────────────────────────
      // A wooden arm reaching out from the wall over the doorstep.
      { geometry: new THREE.BoxGeometry(0.07, 0.07, 0.55), color: WOOD,
        position: [0.9, 1.87, 1.05] },
      ...lantern(),

      // ── leaning stovepipe chimney ─────────────────────────────────────────
      // An iron pipe tipping hard out of the hat's left shoulder.
      { geometry: new THREE.CylinderGeometry(0.18, 0.24, 1.5, 8), color: PIPE,
        position: [-1.5, 2.5, -0.35], rotation: [0, 0, 0.55] },
      { geometry: new THREE.CylinderGeometry(0.26, 0.2, 0.16, 8), color: WALL_DARK,
        position: [-1.82, 3.0, -0.35], rotation: [0, 0, 0.55] },

      // ── smoke puffs (driven by the smoke loop) ────────────────────────────
      ...smoke.parts,
    ];
  },
  animations: [
    // Idle — the hut feels inhabited: the lantern swings gently by the door
    // while the round window pulses potion-green.
    {
      id: "idle",
      label: "Lantern and glow",
      duration: 3.2,
      loop: true,
      tracks: [
        sway("lantern", LANTERN_HOOK, { axis: "x", amount: 0.22 }),
        {
          target: "glow",
          pivot: GLOW_AT,
          scale: [
            { t: 0, value: 1, ease: "sine" },
            { t: 0.8, value: 1.3, ease: "sine" },
            { t: 1.6, value: 1, ease: "sine" },
            { t: 2.4, value: 1.3, ease: "sine" },
            { t: 3.2, value: 1, ease: "sine" },
          ],
        },
      ],
    },
    // Smoke — the signature move: something is brewing, and the leaning
    // stovepipe puffs it away on its own loop.
    {
      id: "smoke",
      label: "Stovepipe smoke",
      duration: 4.8,
      loop: true,
      tracks: [...smoke.tracks],
    },
  ],
});
